import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Calendar } from 'react-date-range/dist'
import { vi } from 'react-date-range/dist/locale'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useSelector } from 'react-redux'
import { languages } from '../../../configs/language'
import { FORMAT, formatDateTime } from '../../helpers/datetime'
import EventEmitter, {
  BACK_MONTH_DATE_PICKER,
  NEXT_MONTH_DATE_PICKER,
  SET_MONTH_DATE_PICKER,
  SET_SINGLE_DATE,
  SET_YEAR_DATE_PICKER,
} from '../../helpers/EventEmitter'
import { UseTimeZone } from '../hooks/useTimeZone'
import { Element } from '../html/element'
import { Icon } from '../html/icon'
import { ButtonSubmit } from './ButtonSubmit'
import { enMonths, viMonths } from './constants'
import { checkMaxMonth, checkMinMonth, convertResultDate } from './helper'
import classes from './index.module.scss'
import SelectMonth from './SelectMonth'
import SelectYear from './SelectYear'

const SingleDate = ({ onSubmit, defaultDate, minDate, maxDate }) => {
  const timezone = UseTimeZone()

  const locale = useSelector((state) => state.i18n.locale)

  const disableAsyncMonthYear = useRef(false)

  const defaultDateConverted = formatDateTime(
    defaultDate,
    FORMAT.DATE_PICKER,
    locale,
    timezone,
  )

  const [date, setDate] = useState(new Date(defaultDateConverted))
  const [stateMonthYear, setStateMonthYear] = useState({
    month: new Date(defaultDateConverted).getMonth(),
    year: new Date(defaultDateConverted).getFullYear(),
  })
  const [isSelectMonth, setIsSelectMonth] = useState(false)
  const [isSelectYear, setIsSelectYear] = useState(false)

  useEffect(() => {
    const changeDateFromInput = (date) => {
      setDate(date)
      setStateMonthYear({ month: date.getMonth(), year: date.getFullYear() })
      disableAsyncMonthYear.current = true
    }
    EventEmitter.subscribe(SET_SINGLE_DATE, changeDateFromInput)

    return () => EventEmitter.unsubscribe(SET_SINGLE_DATE, changeDateFromInput)
  }, [])

  useEffect(() => {
    if (!disableAsyncMonthYear.current) {
      EventEmitter.dispatch(SET_MONTH_DATE_PICKER, stateMonthYear.month)
      disableAsyncMonthYear.current = false
    }
  }, [stateMonthYear.month])

  useEffect(() => {
    if (!disableAsyncMonthYear.current) {
      EventEmitter.dispatch(SET_YEAR_DATE_PICKER, stateMonthYear.year)
      disableAsyncMonthYear.current = false
    }
  }, [stateMonthYear.year])

  const nextMonth = () => {
    const newDate = new Date(
      new Date(date.setMonth(date.getMonth() + 1)).setDate(1),
    )
    const maxDateObj = new Date(maxDate)

    EventEmitter.dispatch(
      SET_SINGLE_DATE,
      maxDateObj < newDate ? maxDateObj : newDate,
    )

    EventEmitter.dispatch(NEXT_MONTH_DATE_PICKER)
  }

  const backMonth = () => {
    const newDate = new Date(
      new Date(date.setMonth(date.getMonth() - 1)).setDate(1),
    )
    const minDateObj = new Date(minDate)

    EventEmitter.dispatch(
      SET_SINGLE_DATE,
      minDateObj > newDate ? minDateObj : newDate,
    )

    EventEmitter.dispatch(BACK_MONTH_DATE_PICKER)
  }

  const onShownDateChange = (newDate) => {
    setStateMonthYear({
      month: newDate.getMonth(),
      year: newDate.getFullYear(),
    })
  }

  const changeDate = (date) => setDate(date)

  const enableSelectMonth = () => setIsSelectMonth(true)

  const disableSelectMonth = () => setIsSelectMonth(false)

  const enableSelectYear = () => {
    setIsSelectYear(true)
    disableSelectMonth()
  }

  const disableSelectYear = () => {
    const isValidMinMonth = checkMinMonth(
      stateMonthYear.year,
      minYear,
      stateMonthYear.month,
      minMonth,
    )

    if (!isValidMinMonth) {
      setStateMonthYear({ ...stateMonthYear, month: minMonth })
    }

    const isValidMaxMonth = checkMaxMonth(
      stateMonthYear.year,
      maxYear,
      stateMonthYear.month,
      maxMonth,
    )

    if (!isValidMaxMonth) {
      setStateMonthYear({ ...stateMonthYear, month: maxMonth })
    }

    setIsSelectYear(false)

    enableSelectMonth()
  }

  const submit = () => onSubmit(convertResultDate(date, timezone))

  const months = locale === languages.vi ? viMonths : enMonths

  const minMonth = minDate && new Date(minDate).getMonth()

  const minYear = minDate && new Date(minDate).getFullYear()

  const maxMonth = maxDate && new Date(maxDate).getMonth()

  const maxYear = maxDate && new Date(maxDate).getFullYear()

  return (
    <>
      {isSelectMonth && (
        <SelectMonth
          stateMonthYear={stateMonthYear}
          setStateMonthYear={setStateMonthYear}
          disableSelectMonth={disableSelectMonth}
          enableSelectYear={enableSelectYear}
          minMonth={minMonth}
          minYear={minYear}
          maxMonth={maxMonth}
          maxYear={maxYear}
          minDate={new Date(minDate)}
          maxDate={new Date(maxDate)}
        />
      )}
      {isSelectYear && (
        <SelectYear
          stateMonthYear={stateMonthYear}
          setStateMonthYear={setStateMonthYear}
          disableSelectYear={disableSelectYear}
          minMonth={minMonth}
          minYear={minYear}
          maxMonth={maxMonth}
          maxYear={maxYear}
        />
      )}
      <Element
        className={`${classes.calendarContainer} ${
          isSelectMonth || isSelectYear ? 'dNone' : ''
        }`}
        style={{ backgroundColor: '#ffffff' }}
      >
        <div
          className={`${classes.monthYearContainer} ${classes.pd0} ${classes.monthYearSingle}`}
        >
          <div
            className={`${classes.monthYear} justifyContentBetween ${classes.w100}`}
          >
            <Icon
              className="icon-arrow-left"
              style={{ fontSize: 10 }}
              onClick={backMonth}
            />
            <Element
              onClick={enableSelectMonth}
              className={classes.monthYearDisplay}
              style={{ fontSize: 14 }}
            >
              {months[stateMonthYear.month]} {stateMonthYear.year}
            </Element>
            <Icon
              className="icon-arrow-right"
              style={{ fontSize: 10 }}
              onClick={nextMonth}
            />
          </div>
        </div>
        <div className={classes.singleDateContainer}>
          <Calendar
            date={date}
            onChange={changeDate}
            locale={locale === languages.vi ? vi : undefined}
            weekStartsOn={1}
            onShownDateChange={onShownDateChange}
            minDate={minDate ? new Date(minDate) : undefined}
            maxDate={maxDate ? new Date(maxDate) : undefined}
          />
        </div>
        <div className={classes.buttonContainer}>
          <ButtonSubmit submit={submit} />
        </div>
      </Element>
    </>
  )
}

SingleDate.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  defaultDate: PropTypes.any,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
}

SingleDate.defaultProps = {
  defaultDate: new Date(),
}

export default SingleDate
